import React from "react";
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Link,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  FormControl,
  InputLabel,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useQuery, useMutation } from "@apollo/client";
import { GET_JOB_APPLICATIONS_BY_JOB } from "../../../../platform/graphql/queries/careJobs"; 
import { UPDATE_JOB_APPLICATION_STATUS } from "../../../../platform/graphql/mutations/careJobs";

type Props = {
  jobId: number;
  jobTitle?: string;
};

const STATUS_OPTIONS = [
  "NEW",
  "REVIEWING",
  "INTERVIEW",
  "OFFERED",
  "HIRED",
  "REJECTED",
  "WITHDRAWN",
] as const;

function StatusChip({ status }: { status: string }) {
  const color =
    status === "NEW" ? "default" :
    status === "REVIEWING" ? "info" :
    status === "INTERVIEW" ? "primary" :
    status === "OFFERED" ? "secondary" :
    status === "HIRED" ? "success" :
    status === "REJECTED" ? "warning" :
    "default";
  return <Chip size="small" label={status} color={color as any} variant="outlined" />;
}

export default function JobApplicationsTable({ jobId, jobTitle }: Props) {
  const [statusFilter, setStatusFilter] = React.useState<string | "ALL">("ALL");

  const { data, loading, error, refetch } = useQuery(GET_JOB_APPLICATIONS_BY_JOB, {
    variables: { jobId, status: statusFilter === "ALL" ? null : statusFilter, limit: 200, offset: 0 },
    fetchPolicy: "cache-and-network",
  });

  const [updateStatus] = useMutation(UPDATE_JOB_APPLICATION_STATUS, {
    onCompleted: () => refetch(),
  });

  const apps = data?.jobApplicationsByJob ?? [];

  const handleStatusChange = async (appId: number, next: string) => {
    await updateStatus({ variables: { id: appId, status: next } });
  };

  return (
    <Box>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" sx={{ mb: 1.5 }} spacing={1}>
        <Typography variant="h6" sx={{ fontSize: { xs: "1.0625rem", sm: "1.125rem" } }}>
          Applicants{jobTitle ? ` — ${jobTitle}` : ""}
        </Typography>

        <Stack direction="row" spacing={1}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="status-filter-label">Filter status</InputLabel>
            <Select
              labelId="status-filter-label"
              label="Filter status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="ALL">All</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {String((error as any)?.message || error)}
        </Alert>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Applied</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Applicant</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Contact</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Resume</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && apps.length === 0 ? (
            <TableRow><TableCell colSpan={5}>Loading…</TableCell></TableRow>
          ) : apps.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5}>
                <Typography variant="body2" color="text.secondary">No applications yet.</Typography>
              </TableCell>
            </TableRow>
          ) : (
            apps.map((a: any) => (
              <TableRow key={a.id}>
                <TableCell>
                  {a.createdAt ? new Date(a.createdAt).toLocaleString() : "—"}
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontWeight: 600 }}>{a.applicantName || "—"}</Typography>
                  {a.coverLetter && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      Cover: {a.coverLetter.slice(0, 80)}{a.coverLetter.length > 80 ? "…" : ""}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{a.applicantEmail || "—"}</Typography>
                  <Typography variant="caption" color="text.secondary">{a.applicantPhone || ""}</Typography>
                </TableCell>
                <TableCell>
                  {a.resumeUrl ? (
                    <Tooltip title="Open resume">
                      <IconButton size="small" component={Link} href={a.resumeUrl} target="_blank" rel="noreferrer">
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Typography variant="caption" color="text.secondary">—</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <StatusChip status={a.status} />
                    <Select
                      size="small"
                      value={a.status}
                      onChange={(e) => handleStatusChange(Number(a.id), String(e.target.value))}
                      sx={{ ml: 1, minWidth: 140 }}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </Select>
                  </Stack>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Box>
  );
}
